import greatCircle from "@turf/great-circle";
import length from "@turf/length";
import type { Feature, LineString, Position } from "geojson";

/**
 * Geographic point ([latitude, longitude]) used by Leaflet (`L.LatLng`).
 * Note: Leaflet expects (lat, lng) but GeoJSON / Turf use (lng, lat).
 *
 * Kept as a mutable tuple because Leaflet's `LatLngExpression` rejects readonly
 * tuples (it forwards them to internal mutation paths).
 */
export type LatLng = [number, number];

/**
 * Build a great-circle (orthodromic) GeoJSON LineString between two points.
 * Used as a frontend fallback when the backend has not computed the route yet,
 * specifically for AIR transport.
 *
 * `npoints` controls the smoothness of the curve when projected on a Mercator
 * map. 64 is a good default trade-off for medium screens.
 */
export const buildGreatCircle = (origin: LatLng, destination: LatLng, npoints = 64): Feature<LineString> => {
    const start: Position = [origin[1], origin[0]]; // [lng, lat]
    const end: Position = [destination[1], destination[0]];

    return greatCircle(start, end, { npoints }) as Feature<LineString>;
};

/**
 * Compute a great-circle distance in kilometres between two points.
 * Mirrors what the backend would return for AIR mode without the network round-trip.
 */
export const greatCircleDistanceKm = (origin: LatLng, destination: LatLng): number => {
    const line = buildGreatCircle(origin, destination, 32);
    return length(line, { units: "kilometers" });
};
